// oxlint-disable-next-line no-restricted-imports
import { Skeleton as MantineSkeleton, Flex } from "@mantine/core";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";

import { BUTTON_SIZES } from "../Button/Button";
import { Text, TEXT_LINE_HEIGHTS } from "../Typographies/Text";
import { TITLE_LINE_HEIGHTS } from "../Typographies/Title";
import classes from "./Skeleton.module.css";

type SkeletonVariant = "line" | "circle" | "rectangle" | "text-block";

const VARIANT_DEFAULTS: Record<
  SkeletonVariant,
  { circle: boolean; borderRadius: string; height: string; width: string }
> = {
  line: {
    circle: false,
    borderRadius: radius.tiny,
    height: spacing[2],
    width: "100%",
  },
  circle: {
    circle: true,
    borderRadius: radius.xxxl,
    height: spacing[10],
    width: spacing[10],
  },
  rectangle: {
    circle: false,
    borderRadius: radius.xs,
    height: spacing[20],
    width: "100%",
  },
  "text-block": {
    circle: false,
    borderRadius: radius.tiny,
    height: spacing[4],
    width: "100%",
  },
};

type SkeletonProps = {
  variant?: SkeletonVariant;
  width?: string | number;
  height?: string | number;
  radius?: string | number;
  visible?: boolean;
  animate?: boolean;
  children?: React.ReactNode;
};

type TextSkeletonProps = {
  size?: "base" | "small" | "tiny";
  width?: string | number;
};

type TitleSkeletonProps = {
  size?: "large" | "medium" | "small" | "tiny";
  width?: string | number;
};

type ButtonSkeletonProps = {
  size?: "sm" | "md" | "lg";
  width?: string | number;
};

type InputSkeletonProps = {
  size?: "sm" | "md" | "lg";
  width?: string | number;
  label?: string;
};

const Skeleton = ({
  variant = "rectangle",
  width,
  height,
  radius: radiusProp,
  visible = true,
  animate = true,
  children,
}: SkeletonProps) => {
  const base = VARIANT_DEFAULTS[variant];

  const resolvedHeight = height ?? base.height;
  const resolvedWidth = base.circle ? undefined : (width ?? base.width);
  const resolvedRadius = radiusProp ?? base.borderRadius;

  return (
    <MantineSkeleton
      circle={base.circle}
      height={resolvedHeight}
      width={resolvedWidth}
      radius={resolvedRadius}
      visible={visible}
      animate={animate}
      classNames={{ root: classes.root }}
      style={
        {
          "--skeleton-shimmer-color": colors.bg.elevation.quaternary,
          "--skeleton-shimmer-color-dark": colors.bg.elevation.tertiary,
        } as React.CSSProperties
      }
    >
      {children}
    </MantineSkeleton>
  );
};

// TextSkeleton — same size values as the Text atom
const TextSkeleton = ({ size = "base", width = "100%" }: TextSkeletonProps) => (
  <Skeleton variant="line" height={TEXT_LINE_HEIGHTS[size]} width={width} />
);

// TitleSkeleton — same size values as the Title atom (line-heights)
const TitleSkeleton = ({ size = "medium", width = "100%" }: TitleSkeletonProps) => (
  <Skeleton variant="line" height={TITLE_LINE_HEIGHTS[size]} width={width} />
);

// ButtonSkeleton — same size values as the Button atom
const ButtonSkeleton = ({ size = "md", width }: ButtonSkeletonProps) => (
  <Skeleton height={BUTTON_SIZES[size].height} width={width ?? spacing[24]} radius={radius.xl} />
);

// InputSkeleton — same size values as Mantine inputs
const InputSkeleton = ({ label, size = "md", width }: InputSkeletonProps) => (
  <Flex direction="column" gap={spacing[1]}>
    <Text type="medium" size="small">
      {label}
    </Text>
    <Skeleton height={BUTTON_SIZES[size].height} width={width ?? "100%"} radius={radius.xs} />
  </Flex>
);

export { Skeleton, TextSkeleton, TitleSkeleton, ButtonSkeleton, InputSkeleton };
export type { SkeletonVariant };
